'use client'
import React from 'react'
import type { ReactNode } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '../helpers/index.js'
import { type VariantProps, cva } from 'class-variance-authority'
import type { ComponentPropsWithout, RemovedProps } from '../helpers/component-props.js'

// 定义 link 的样式变体
const linkVariants = cva('tracking  hover:text-brand-0 font-bold underline transition-colors', {
  variants: {
    size: {
      base: 'text-text-2', // 中
      lg: 'text-text-3', // 大
    },
    variant: {
      secondary: 'text-info-secondary hover:text-info-primary',
      primary: 'text-info-primary hover:text-brand-0',
    },
  },
})
// 定义组件类型，继承 a 标签标准属性
type LinkElement = React.ElementRef<'a'>
type LinkProps = {
  /** 定义子组件作为父组件的类型,详细使用方式请参考radix-ui */
  asChild?: boolean
  /** 类名*/
  className?: string
  /** 禁用链接 */
  disabled?: boolean
  /** 后置图标*/
  suffixIcon?: ReactNode
  /** 跳转链接*/
  href?: string
  /** 样式*/
  style?: React.CSSProperties
  /** 点击事件，禁用状态无法触发*/
  onClick?: Function
} & VariantProps<typeof linkVariants> &
  ComponentPropsWithout<'a', RemovedProps>

/**
 * Link - 链接
 *
 * @description 可点击的链接组件，支持不同尺寸、样式变体、禁用状态和后置图标
 */
const Link = React.forwardRef<LinkElement, LinkProps>(
  (
    {
      children,
      size = 'base',
      variant = 'primary',
      className,
      asChild = false,
      href,
      style,
      disabled = false,
      suffixIcon,
      onClick,
      ...props // 这里会包含 href、target、rel 等标准 anchor 属性
    }: LinkProps,
    forwardedRef
  ) => {
    const Comp = asChild ? Slot : 'a'

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      !disabled && onClick?.(event)
      if (disabled || !href) {
        event.preventDefault()
        return
      }
    }

    return (
      <Comp
        ref={forwardedRef}
        onClick={handleClick}
        href={disabled ? undefined : href}
        className={cn(linkVariants({ size, variant }), className, {
          'flex items-center': suffixIcon,
          'cursor-pointer': !disabled,
          'text-info-quaternary cursor-not-allowed hover:text-info-quaternary': disabled,
        })}
        {...(style ? { style: style } : null)}
        {...props}
      >
        <>
          {children}
          {suffixIcon && <span className="svg">{suffixIcon}</span>}
        </>
      </Comp>
    )
  }
)
Link.displayName = 'Link'

export default Link
export type { LinkProps }
